import { Box, Button, Card, Chip, Pagination, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { FaUsers, FaArrowsRotate } from "react-icons/fa6";
import { useQuery } from "@tanstack/react-query";
import { getCustomersLogs } from "../../helpers/http_customers_log_requests";
import { getCustomers } from "../../helpers/http_customers_requests";
import PageLayout from "../../components/PageLayout";
import TableLoading from "../../components/TableLoading";
import CustomFilterBar from "../../components/common/CustomFilterBar";
import { useState } from "react";
import { formatDateBR } from "../../utils/functions";
import { muted, tableBodyCellSx, tableCardSx, tableHeadCellSx, tableRowSx } from "../../components/common/tableStyles";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PER_PAGE = 20;

const ACTION_CONFIG = {
    get: { label: "Visualização", color: "#2e7d32", bg: "rgba(46, 125, 50, 0.12)" },
    view: { label: "Visualização", color: "#2e7d32", bg: "rgba(46, 125, 50, 0.12)" },
    post: { label: "Criação", color: "#1565c0", bg: "rgba(21, 101, 192, 0.12)" },
    patch: { label: "Edição", color: "#ed6c02", bg: "rgba(237, 108, 2, 0.12)" },
    put: { label: "Edição", color: "#ed6c02", bg: "rgba(237, 108, 2, 0.12)" },
    update: { label: "Atualização", color: "#ed6c02", bg: "rgba(237, 108, 2, 0.12)" },
    delete: { label: "Exclusão", color: "#d32f2f", bg: "rgba(211, 47, 47, 0.12)" },
    login: { label: "Login", color: "#1565c0", bg: "rgba(21, 101, 192, 0.12)" },
    logout: { label: "Logout", color: "#616161", bg: "rgba(97, 97, 97, 0.12)" },
};

function ActionCell({ action }) {
    const key = (action ?? "").toLowerCase().trim();
    const config = ACTION_CONFIG[key] ?? { label: key || "-", color: "#616161", bg: "rgba(97, 97, 97, 0.12)" };
    const rawLabel = action ? `Ação: ${action}` : "Sem ação";
    return (
        <Tooltip title={rawLabel} arrow placement="top">
            <Chip
                label={config.label}
                size="small"
                sx={{
                    backgroundColor: config.bg,
                    color: config.color,
                    fontWeight: 600,
                    border: "none",
                }}
            />
        </Tooltip>
    );
}

export default function CustomersLog() {
    const [filterValues, setFilterValues] = useState({
        page: 1,
        per_page: String(DEFAULT_PER_PAGE),
    });

    const handleFilterChange = (field, value) => {
        setFilterValues((prev) => {
            const next = { ...prev, [field]: value };
            if (field !== "page" && field !== "per_page") {
                next.page = 1;
            }
            return next;
        });
    };

    const handlePageChange = (_event, page) => {
        setFilterValues((prev) => ({ ...prev, page }));
    };

    const { data, isFetching, refetch } = useQuery({
        queryKey: ["getCustomersLogs", filterValues],
        queryFn: () => getCustomersLogs(filterValues),
        staleTime: 1000 * 30,
    });

    const customersQuery = useQuery({
        queryKey: ["getCustomersForLog"],
        queryFn: () => getCustomers({}),
        staleTime: 1000 * 60,
    });

    const response = data?.data ?? {};
    const items = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.items)
            ? response.items
            : [];
    const total = response.total ?? response.total_count ?? items.length;
    const perPage = Number(filterValues.per_page) || DEFAULT_PER_PAGE;
    const totalPages = Math.max(1, Math.ceil(total / perPage));

    const customers = customersQuery?.data?.data?.customers ?? [];
    const customerOptions = customers.map((c) => ({
        value: String(c.id),
        label: c.complete_name || c.cnpj || `#${c.id}`,
    }));

    const filters = [
        { field: "customer_id", type: "select", label: "Cliente", options: customerOptions },
        { field: "date_from", type: "datepicker", label: "Data inicial" },
        { field: "date_to", type: "datepicker", label: "Data final" },
        {
            field: "per_page",
            type: "select",
            label: "Por página",
            options: PER_PAGE_OPTIONS.map((n) => ({ value: String(n), label: String(n) })),
        },
    ];

    const formatDate = (value) => formatDateBR(value) || "-";

    const startItem = total === 0 ? 0 : (Number(filterValues.page) || 1) * perPage - perPage + 1;
    const endItem = Math.min((Number(filterValues.page) || 1) * perPage, total);

    if (isFetching && !data) {
        return <TableLoading />;
    }

    return (
        <PageLayout icon={FaUsers} pageTitle="Log de Clientes">
            <Card elevation={0} sx={tableCardSx}>
                <CustomFilterBar
                    filters={filters}
                    values={filterValues}
                    onChange={handleFilterChange}
                    actions={
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                            <Tooltip title="Atualizar lista" arrow>
                                <Button size="small" startIcon={<FaArrowsRotate />} onClick={() => refetch()} disabled={isFetching}>
                                    Atualizar
                                </Button>
                            </Tooltip>
                            <Typography variant="body2" sx={{ ml: "auto", color: muted, textAlign: "left" }}>
                                {startItem} a {endItem} de {total} registro{total !== 1 ? "s" : ""}
                            </Typography>
                        </Box>
                    }
                />
                <Box overflow="auto">
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell align="left" sx={tableHeadCellSx}>Data/Hora</TableCell>
                                <TableCell align="left" sx={tableHeadCellSx}>Ação</TableCell>
                                <TableCell align="left" sx={tableHeadCellSx}>Cliente</TableCell>
                                <TableCell align="left" sx={tableHeadCellSx}>Detalhes</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isFetching ? (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ ...tableBodyCellSx, py: 4, textAlign: "center" }}>
                                        Carregando...
                                    </TableCell>
                                </TableRow>
                            ) : items.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={4} sx={{ ...tableBodyCellSx, py: 6, textAlign: "center" }}>
                                        <Typography sx={{ color: muted }}>Nenhum registro encontrado.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((row, index) => (
                                    <TableRow key={row.id ?? index} hover sx={tableRowSx}>
                                        <TableCell align="left" sx={tableBodyCellSx}>{formatDate(row.created_at ?? row.date ?? row.occurred_at)}</TableCell>
                                        <TableCell align="left" sx={tableBodyCellSx}><ActionCell action={row.action} /></TableCell>
                                        <TableCell align="left" sx={tableBodyCellSx}>{row.user_name ?? (row.user_id ? `#${row.user_id}` : "-")}</TableCell>
                                        <TableCell align="left" sx={{ ...tableBodyCellSx, maxWidth: 360 }}>
                                            <Tooltip title={row.description ?? row.entity_id ?? "-"} arrow placement="top" enterDelay={400}>
                                                <Typography noWrap sx={{ maxWidth: 340, textAlign: "left" }} variant="body2" component="span">
                                                    {row.description ?? row.entity_id ?? "-"}
                                                </Typography>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Box>
                {totalPages > 1 && (
                    <Box sx={{ py: 1.5, px: 2, display: "flex", justifyContent: "flex-end", borderTop: "1px solid", borderColor: "divider" }}>
                        <Pagination
                            count={totalPages}
                            page={Number(filterValues.page) || 1}
                            onChange={handlePageChange}
                            color="secondary"
                            shape="rounded"
                            size="small"
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                )}
            </Card>
        </PageLayout>
    );
}
